<template>
  <div class="course-layout-v5">
    <Loading v-model:active="loadingStore.isLoading" :can-cancel="false" :is-full-page="true" />

    <header class="shell-header">
      <div class="shell-header__brand">
        <router-link to="/caduceus/dashboard" class="brand-link">
          <img src="@/assets/img/caduceus.png" alt="Caduceus" class="brand-link__logo">
          <div class="brand-link__copy">
            <span>{{ isPreview ? 'Preview' : 'Aluno' }}</span>
            <strong>{{ course?.title || 'Curso' }}</strong>
          </div>
        </router-link>
      </div>

      <div class="shell-header__actions">
        <div class="student-badge">
          <span>Aluno</span>
          <strong>{{ studentName || 'Visitante' }}</strong>
        </div>
        <button class="menu-button mobile-only" @click="toggleSidebar">
          <i class="fa fa-bars"></i>
        </button>
      </div>
    </header>

    <div class="layout-shell">
      <main class="main-stage">
        <section class="player-stage" v-if="selectedContent === 'lesson'">
          <div class="player-stage__frame" v-if="currentLesson.contentType === 'YOUTUBE'">
            <LiteYouTubeEmbed
              :id="getYoutubeId(currentLesson.contentUrl)"
              class="video-embed"
              title="Vídeo da Lição"
            />
          </div>

          <div class="player-stage__frame player-stage__frame--document" v-else-if="currentLesson.contentType === 'PDF'">
            <embed
              :src="'https://docs.google.com/gview?embedded=true&url=' + currentLesson.contentUrl"
              type="application/pdf"
              width="100%"
              height="100%"
            />
          </div>

          <div class="player-stage__frame" v-else-if="currentLesson.contentType === 'PANDA'">
            <iframe
              :src="currentPandaVideo.contentUrl"
              style="border:none;"
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture"
              allowfullscreen
              width="100%"
              height="100%"
              fetchpriority="high"
            ></iframe>
          </div>

          <div class="player-stage__quiz" v-else-if="currentLesson.contentType === 'QUIZ'">
            <QuizPlayer
              :lesson="currentLesson"
              :course-id="course?.id"
              @quiz-completed="handleQuizCompleted"
            />
          </div>

          <div class="player-stage__empty" v-else>
            <i class="fas fa-file-alt"></i>
            <h3>Conteúdo indisponível</h3>
            <p>Este formato ainda não possui visualização neste layout.</p>
          </div>
        </section>

        <section class="status-card status-card--blocked" v-if="selectedContent === 'blocked'">
          <div class="status-card__icon">
            <i class="fas fa-lock"></i>
          </div>
          <div>
            <p class="status-card__eyebrow">Pré-requisito</p>
            <h3>Conteúdo bloqueado</h3>
            <p>{{ lessonBlockedInfo?.blockedReason || 'Esta aula está bloqueada.' }}</p>
            <p v-if="lessonBlockedInfo?.requiredQuizLessonTitle">
              Complete o quiz <strong>{{ lessonBlockedInfo.requiredQuizLessonTitle }}</strong> para continuar.
            </p>
          </div>
        </section>

        <section class="certificate-stage" v-if="selectedContent === 'certificate'">
          <div class="certificate-stage__head">
            <div>
              <p class="section-label">Conquista</p>
              <h3>Certificado disponível</h3>
            </div>
            <a v-if="certificateUrl" :href="certificateUrl" download class="primary-action">
              <i class="fa fa-download"></i>
              <span>Baixar certificado</span>
            </a>
          </div>
          <iframe v-if="certificateUrl" :src="certificateUrl" frameborder="0"></iframe>
        </section>

        <section class="lesson-overview">
          <div class="lesson-overview__copy">
            <p class="section-label">{{ currentModule?.title || 'Aula atual' }}</p>
            <h1>{{ contentTitle }}</h1>
            <p>{{ contentSubtitle }}</p>
          </div>

          <div class="lesson-overview__meta" v-if="selectedContent === 'lesson'">
            <div class="meta-pill">
              <span>Etapa</span>
              <strong>{{ currentLessonIndexLabel }}</strong>
            </div>
            <div class="meta-pill">
              <span>Tipo</span>
              <strong>{{ getLessonType(currentLesson.contentType) }}</strong>
            </div>
          </div>
        </section>

        <section class="lesson-navigation" v-if="selectedContent === 'lesson'">
          <button class="ghost-action" :disabled="!previousLesson" @click="goToLesson(previousLesson)">
            <i class="fa fa-arrow-left"></i>
            <span>Aula anterior</span>
          </button>
          <button class="primary-action" :disabled="!nextLesson" @click="goToLesson(nextLesson)">
            <span>Próxima aula</span>
            <i class="fa fa-arrow-right"></i>
          </button>
        </section>
      </main>

      <aside class="sidebar" :class="{ 'sidebar--open': sidebarOpen }">
        <div class="sidebar__surface">
          <div class="sidebar__top">
            <div>
              <p class="section-label">Trilha</p>
              <h2>Conteúdo do curso</h2>
            </div>
            <button class="menu-button mobile-only" @click="toggleSidebar">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <div class="sidebar__stats">
            <div class="sidebar-stat">
              <span>Liberadas</span>
              <strong>{{ enabledLessons.length }}</strong>
            </div>
            <div class="sidebar-stat">
              <span>Atual</span>
              <strong>{{ currentLessonIndexLabel }}</strong>
            </div>
          </div>

          <div class="module-stack">
            <section v-for="(module, moduleIndex) in course?.modules" :key="module.id" class="module-panel">
              <button class="module-panel__header" @click="toggleModule(moduleIndex)">
                <div>
                  <p class="module-panel__index">Módulo {{ moduleIndex + 1 }}</p>
                  <h3>{{ module.title }}</h3>
                </div>
                <div class="module-panel__header-meta">
                  <span>{{ getEnabledLessonsCount(module) }}</span>
                  <i class="fas fa-chevron-down" :class="{ 'is-open': expandedModules[moduleIndex] }"></i>
                </div>
              </button>

              <transition name="module-expand">
                <div v-show="expandedModules[moduleIndex]" class="lesson-stack">
                  <button
                    v-for="lesson in module.lessons"
                    v-show="lesson.isEnabled"
                    :key="lesson.id"
                    class="lesson-row"
                    :class="{ 'lesson-row--active': currentLesson.id === lesson.id }"
                    @click="selectLesson(module, lesson)"
                  >
                    <span class="lesson-row__order">{{ getLessonOrder(lesson.id) }}</span>
                    <span class="lesson-row__text">
                      <strong>{{ lesson.title }}</strong>
                      <small>{{ getLessonType(lesson.contentType) }}</small>
                    </span>
                  </button>
                </div>
              </transition>
            </section>

            <button
              v-if="isCertificateAvailable && !isPreview"
              class="certificate-shortcut"
              @click="selectCertificate"
            >
              <span class="certificate-shortcut__icon"><i class="fas fa-award"></i></span>
              <span class="certificate-shortcut__text">
                <strong>Certificado</strong>
                <small>Visualizar e baixar</small>
              </span>
            </button>
          </div>
        </div>
      </aside>
    </div>

    <div class="sidebar-backdrop" :class="{ 'sidebar-backdrop--active': sidebarOpen }" @click="toggleSidebar"></div>
  </div>
</template>

<script setup>
import ApiService from '@/services/ApiService';
import { tratarError } from '@/axiosConfig';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import LiteYouTubeEmbed from 'vue-lite-youtube-embed';
import { storeToRefs } from 'pinia';
import { useLoadinStore } from '@/stores/loadingStore';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import QuizPlayer from '@/components/quiz/QuizPlayer.vue';
import { JWTHandler } from '@/jwtHandler';

const loadingStore = storeToRefs(useLoadinStore());
const route = useRoute();
const slug = route.params.slug;
const courseId = route.params.id;
const isPreview = !!courseId;

const studentName = ref('');
const course = ref(null);
const isCertificateAvailable = ref(false);
const currentLesson = ref({});
const currentModule = ref({});
const selectedContent = ref('lesson');
const certificateUrl = ref('');
const currentPandaVideo = ref({});
const sidebarOpen = ref(false);
const expandedModules = ref({});
const lessonBlockedInfo = ref(null);

const enabledLessons = computed(() =>
  course.value?.modules?.flatMap((module) =>
    (module.lessons || [])
      .filter((lesson) => lesson.isEnabled)
      .map((lesson) => ({ module, lesson })),
  ) || [],
);

const currentLessonIndex = computed(() =>
  enabledLessons.value.findIndex((entry) => entry.lesson.id === currentLesson.value.id),
);

const currentLessonIndexLabel = computed(() => {
  if (currentLessonIndex.value < 0) {
    return '0/0';
  }
  return `${currentLessonIndex.value + 1}/${enabledLessons.value.length}`;
});

const previousLesson = computed(() => {
  if (currentLessonIndex.value <= 0) {
    return null;
  }
  return enabledLessons.value[currentLessonIndex.value - 1];
});

const nextLesson = computed(() => {
  if (currentLessonIndex.value < 0 || currentLessonIndex.value >= enabledLessons.value.length - 1) {
    return null;
  }
  return enabledLessons.value[currentLessonIndex.value + 1];
});

const contentTitle = computed(() => {
  if (selectedContent.value === 'certificate') {
    return 'Seu certificado';
  }
  if (selectedContent.value === 'blocked') {
    return 'Acesso restrito';
  }
  return currentLesson.value.title || 'Selecione uma aula';
});

const contentSubtitle = computed(() => {
  if (selectedContent.value === 'certificate') {
    return 'Acesse o comprovante de conclusão do curso.';
  }
  if (selectedContent.value === 'blocked') {
    return 'Esta etapa depende de um conteúdo anterior.';
  }
  if (currentLesson.value.contentType === 'QUIZ') {
    return 'Responda ao quiz para concluir esta etapa da trilha.';
  }
  return 'Layout com foco maior no player e navegação lateral mais discreta.';
});

onMounted(async () => {
  try {
    let response;
    if (isPreview) {
      response = await ApiService.get(`/courses/${courseId}/preview`);
      course.value = response.data.course;
      studentName.value = response.data.student || JWTHandler.getUserData()?.fullName || 'Preview';
      isCertificateAvailable.value = false;
    } else {
      response = await ApiService.get(`/enrollments/${slug}/course`);
      course.value = response.data.course;
      studentName.value = response.data.student;
      isCertificateAvailable.value = response.data.isCertificateAvailable;
    }

    if (course.value?.modules?.length > 0 && course.value.modules[0].lessons?.length > 0) {
      expandedModules.value[0] = true;
      const firstLesson = course.value.modules[0].lessons.find((lesson) => lesson.isEnabled);
      if (firstLesson) {
        await selectLesson(course.value.modules[0], firstLesson);
      }
    }
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar curso:', error);
  }
});

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const toggleModule = (index) => {
  expandedModules.value[index] = !expandedModules.value[index];
};

const checkLessonAccess = async (lesson) => {
  if (isPreview && lesson.contentType !== 'PANDA') {
    return {
      isBlocked: false,
      contentUrl: lesson.contentUrl,
    };
  }

  try {
    const response = await ApiService.get(`/lessons/${lesson.id}/panda`);
    return {
      isBlocked: response.data.isBlocked || false,
      blockedReason: response.data.blockedReason,
      requiredQuizLessonTitle: response.data.requiredQuizLessonTitle,
      contentUrl: response.data.contentUrl,
    };
  } catch (error) {
    tratarError(error);
    console.error('Erro ao verificar acesso à lição:', error);
    return { isBlocked: false, contentUrl: lesson.contentUrl };
  }
};

const selectLesson = async (module, lesson) => {
  currentLesson.value = lesson;
  currentModule.value = module;
  selectedContent.value = 'lesson';
  lessonBlockedInfo.value = null;

  if (window.innerWidth < 1180) {
    sidebarOpen.value = false;
  }

  if (!isPreview && (lesson.contentType === 'YOUTUBE' || lesson.contentType === 'PDF' || lesson.contentType === 'QUIZ')) {
    const accessInfo = await checkLessonAccess(lesson);
    if (accessInfo.isBlocked) {
      lessonBlockedInfo.value = accessInfo;
      selectedContent.value = 'blocked';
      return;
    }
  }

  if (lesson.contentType === 'PANDA') {
    await loadPandaVideo(lesson);
  }
};

const loadPandaVideo = async (lesson) => {
  currentPandaVideo.value = {};
  const accessInfo = await checkLessonAccess(lesson);

  if (accessInfo.isBlocked) {
    lessonBlockedInfo.value = accessInfo;
    selectedContent.value = 'blocked';
    return;
  }

  currentPandaVideo.value = { contentUrl: accessInfo.contentUrl };
  selectedContent.value = 'lesson';
};

const selectCertificate = async () => {
  if (isPreview) {
    return;
  }

  try {
    const response = await ApiService.get(`/enrollments/certificate/${slug}`);
    certificateUrl.value = response.data;
    selectedContent.value = 'certificate';
  } catch (error) {
    tratarError(error);
    console.error('Erro ao carregar certificado:', error);
  }

  if (window.innerWidth < 1180) {
    sidebarOpen.value = false;
  }
};

const goToLesson = async (entry) => {
  if (!entry) {
    return;
  }
  await selectLesson(entry.module, entry.lesson);
};

const handleQuizCompleted = () => {
  console.log('Quiz completed');
};

const getEnabledLessonsCount = (module) => {
  const count = module.lessons?.filter((lesson) => lesson.isEnabled).length || 0;
  return count === 1 ? '1 aula' : `${count} aulas`;
};

const getLessonType = (contentType) => {
  const types = {
    YOUTUBE: 'Vídeo',
    PANDA: 'Panda Video',
    PDF: 'Documento',
    QUIZ: 'Quiz',
  };
  return types[contentType] || 'Conteúdo';
};

const getYoutubeId = (url) => {
  if (!url) {
    return '';
  }

  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1]?.split('?')[0] || '';
  }

  if (url.includes('v=')) {
    return url.split('v=')[1]?.split('&')[0] || '';
  }

  return url;
};

const getLessonOrder = (lessonId) => {
  const index = enabledLessons.value.findIndex((entry) => entry.lesson.id === lessonId);
  if (index < 0) {
    return '--';
  }
  return String(index + 1).padStart(2, '0');
};
</script>

<style scoped>
.course-layout-v5 {
  --bg: #f5f3ef;
  --surface: #ffffff;
  --surface-soft: #f8f6f2;
  --text: #181512;
  --muted: #72685f;
  --line: #e7dfd4;
  --accent: #1d6a53;
  --accent-dark: #154b3b;
  --shadow: 0 18px 40px rgba(32, 23, 15, 0.06);
  min-height: 100vh;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(29, 106, 83, 0.07), transparent 24%),
    linear-gradient(180deg, #f7f4ef 0%, #f4f1eb 100%);
  color: var(--text);
  font-family: "DM Sans", "Segoe UI", sans-serif;
}

.shell-header,
.shell-header__actions,
.brand-link,
.brand-link__copy,
.student-badge,
.lesson-overview,
.lesson-overview__meta,
.meta-pill,
.lesson-navigation,
.primary-action,
.ghost-action,
.sidebar__top,
.sidebar__stats,
.module-panel__header,
.module-panel__header-meta,
.lesson-row,
.certificate-shortcut,
.certificate-shortcut__text,
.certificate-stage__head {
  display: flex;
  align-items: center;
}

.shell-header {
  justify-content: space-between;
  gap: 16px;
  padding: 8px 6px 18px;
}

.brand-link {
  gap: 12px;
  color: inherit;
  text-decoration: none;
}

.brand-link__logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.brand-link__copy {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.brand-link__copy span,
.section-label,
.module-panel__index,
.status-card__eyebrow,
.student-badge span {
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--muted);
}

.shell-header__actions,
.student-badge,
.lesson-overview__meta,
.lesson-navigation,
.primary-action,
.ghost-action,
.sidebar__stats,
.certificate-shortcut {
  gap: 12px;
}

.student-badge {
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
}

.layout-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 350px;
  gap: 20px;
}

.main-stage {
  display: grid;
  gap: 16px;
}

.player-stage,
.lesson-overview,
.lesson-navigation,
.status-card,
.certificate-stage,
.sidebar__surface {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.76);
  backdrop-filter: blur(18px);
  box-shadow: var(--shadow);
}

.player-stage {
  padding: 18px;
}

.player-stage__frame {
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 22px;
  background: #101010;
}

.player-stage__frame--document {
  background: #ece7de;
}

.player-stage__frame iframe,
.player-stage__frame embed,
.video-embed {
  width: 100%;
  height: 100%;
  display: block;
}

.player-stage__quiz {
  padding: 8px;
}

.player-stage__empty,
.status-card {
  min-height: 220px;
}

.player-stage__empty,
.status-card {
  display: grid;
  place-items: center;
  text-align: center;
}

.player-stage__empty i,
.status-card__icon {
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  margin: 0 auto 16px;
  border-radius: 20px;
  background: rgba(29, 106, 83, 0.1);
  color: var(--accent);
  font-size: 24px;
}

.status-card {
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 8px;
  padding: 30px;
  text-align: left;
}

.lesson-overview {
  justify-content: space-between;
  gap: 18px;
  padding: 26px 28px;
}

.lesson-overview__copy h1 {
  margin: 6px 0 8px;
  font-size: clamp(24px, 2.1vw, 34px);
  line-height: 1.08;
  word-break: break-word;
}

.lesson-overview__copy p:last-child {
  margin: 0;
  color: var(--muted);
}

.lesson-overview__meta {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.meta-pill {
  gap: 10px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: var(--surface-soft);
}

.meta-pill span,
.sidebar-stat span,
.lesson-row__text small {
  color: var(--muted);
}

.lesson-navigation {
  justify-content: space-between;
  padding: 16px 18px;
}

.primary-action,
.ghost-action {
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.primary-action {
  background: var(--accent);
  color: #fff;
}

.ghost-action {
  background: var(--surface-soft);
  color: var(--text);
}

.primary-action:disabled,
.ghost-action:disabled {
  opacity: 0.45;
  cursor: default;
}

.certificate-stage {
  padding: 20px;
}

.certificate-stage__head {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.certificate-stage iframe {
  width: 100%;
  min-height: 720px;
  border: 0;
  border-radius: 18px;
  background: #fff;
}

.sidebar {
  position: relative;
}

.sidebar__surface {
  position: sticky;
  top: 18px;
  padding: 18px;
}

.sidebar__top {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.sidebar__stats {
  margin-bottom: 16px;
}

.sidebar-stat {
  flex: 1;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: var(--surface-soft);
}

.sidebar-stat strong {
  display: block;
  margin-top: 6px;
  font-size: 22px;
}

.module-stack {
  display: grid;
  gap: 12px;
  max-height: calc(100vh - 180px);
  overflow: auto;
  padding-right: 4px;
}

.module-panel {
  border: 1px solid var(--line);
  border-radius: 22px;
  background: rgba(248, 246, 242, 0.7);
}

.module-panel__header {
  width: 100%;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.module-panel__header h3 {
  margin: 4px 0 0;
  font-size: 20px;
  line-height: 1.18;
}

.lesson-stack {
  display: grid;
  gap: 8px;
  padding: 0 12px 12px;
}

.lesson-row {
  width: 100%;
  gap: 12px;
  padding: 12px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.lesson-row:hover,
.lesson-row--active {
  background: #fff;
}

.lesson-row--active {
  outline: 1px solid rgba(29, 106, 83, 0.18);
}

.lesson-row__order {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 999px;
  background: rgba(29, 106, 83, 0.1);
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
}

.lesson-row__text {
  display: grid;
  gap: 4px;
}

.lesson-row__text strong {
  font-size: 14px;
  line-height: 1.32;
}

.certificate-shortcut {
  width: 100%;
  padding: 14px;
  border: 1px solid rgba(29, 106, 83, 0.12);
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(29, 106, 83, 0.1), rgba(218, 189, 150, 0.16));
  text-align: left;
  cursor: pointer;
}

.certificate-shortcut__icon {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.76);
  color: var(--accent-dark);
}

.certificate-shortcut__text {
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

.certificate-shortcut__text small {
  color: var(--muted);
}

.menu-button {
  display: none;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.86);
  cursor: pointer;
}

.sidebar-backdrop {
  position: fixed;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  background: rgba(24, 21, 18, 0.38);
  transition: opacity 0.2s ease;
}

.sidebar-backdrop--active {
  opacity: 1;
  pointer-events: auto;
}

.module-expand-enter-active,
.module-expand-leave-active {
  transition: all 0.18s ease;
}

.module-expand-enter-from,
.module-expand-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.is-open {
  transform: rotate(180deg);
}

@media (max-width: 1179px) {
  .layout-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 30;
    width: min(92vw, 380px);
    height: 100vh;
    transform: translateX(104%);
    transition: transform 0.22s ease;
  }

  .sidebar--open {
    transform: translateX(0);
  }

  .sidebar__surface {
    top: 0;
    height: 100%;
    border-radius: 28px 0 0 28px;
  }

  .menu-button.mobile-only {
    display: grid;
  }
}

@media (max-width: 767px) {
  .course-layout-v5 {
    padding: 12px;
  }

  .shell-header {
    padding: 4px 0 14px;
  }

  .student-badge {
    display: none;
  }

  .lesson-overview,
  .lesson-navigation,
  .status-card {
    padding: 18px;
  }

  .lesson-overview {
    flex-direction: column;
    align-items: flex-start;
  }

  .lesson-overview__meta {
    justify-content: flex-start;
  }

  .lesson-navigation {
    flex-direction: column;
  }

  .primary-action,
  .ghost-action {
    width: 100%;
    justify-content: center;
  }

  .status-card {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .certificate-stage iframe {
    min-height: 520px;
  }
}
</style>
